/**
 * Modes registry (UI-rewrite, IA §3; Three-Layer PRD §3.3). The business app reduces
 * to **five business modes**, and crucially a mode is a *saved query over commitments*,
 * not an editorial folder — which makes the set derivable, not arbitrary (a new kind
 * joins the mode whose query it matches; a new PRODUCT never adds a mode — the
 * five-mode invariant holds for products). Layer 2 (Domains, `domains.ts`) is where the
 * apps live; a new app is a new Domain registry entry under an existing mode.
 *
 *   Verification owner.kind = 'agent'      (Verifier console · readiness · legacy ops)
 *   Work        owner = me, any kind        (Inbox · Issues · Planner · my Goals · Reviews)
 *   Customers   kind ∈ {lead, deal, …}      (Marketing · Sales · Support)
 *   Product     kind ∈ {task, prd, release} (Engineering · Design · Docs)
 *   Operations  kind ∈ {transaction, obligation, job, application, scorecard}
 *                                           (Finance · Legal · Team · Directory · Hiring)
 *   Personal    owner = me, my saved data   (a personal knowledge corpus — NOT a
 *                                            business function; docs/personal/PRD.md)
 *
 * The **sixth mode, Personal**, is the deliberate, documented exception: it is a
 * workspace-scoped personal-data surface (drop in any data → view + search →
 * use as CLI context), not a product and not a saved query over commitments. It
 * carries no business KPIs ("no bars"), keeps its own route (`/personal`, like
 * `/agents`), and is absent from SECTION_REGISTRY. Products still never add a mode.
 *
 * Three modes slice ACROSS kinds by owner (Work, Verification, Personal); three slice BY
 * kind (Customers, Product, Operations). The ATS folds in as Operations' **Hiring**
 * Domain (2026-06-17 — reverting the brief LOCKED #2 promotion to its own mode;
 * "who are we hiring?" is a back-office question alongside Finance/Legal/Team). A
 * mode's generic four views (Dashboard · Queue · Objects · Analytics — the
 * ModeShell scaffold) are the FALLBACK for an unbuilt domain; the real surfaces
 * are Layer-2 Domains.
 *
 * This module is PURE substrate: no flag read, no I/O. The redirect resolver
 * (redirects.ts), the Domain registry (domains.ts), and the nav chrome consume
 * it; the modes pages render from it now that the modes nav is unconditional
 * (the NEXT_PUBLIC_MODES_NAV flag is retired; Three-Layer PRD §6).
 */

// Narrowed from the `@excellent/core` barrel to the two registry leaves
// (RO-0-05). This module is pure path logic and the Vite client imports it for
// the root shell's redirect cascade; the barrel would drag `objectives` →
// `events/engine` → `db` → `security/{keystore,keychain}` into the browser
// bundle behind it (PRD §5 I4 / R1). Identical values, no behaviour change.
import { SECTION_REGISTRY } from "@excellent/core/section-registry";
import type { StreamSlug } from "@excellent/core/stream-registry";

export const MODE_SLUGS = [
  "agents",
  "work",
  "customers",
  "product",
  "operations",
  "personal",
] as const;
export type ModeSlug = (typeof MODE_SLUGS)[number];

export const MODE_VIEWS = ["dashboard", "queue", "objects", "analytics"] as const;
export type ModeView = (typeof MODE_VIEWS)[number];

export interface ModeDef {
  slug: ModeSlug;
  label: string;
  /** One-line description of what the mode is for — the masthead description,
   *  house voice (short, plain, not press-release). Every mode carries one
   *  (docs/design/PAGE-HEADER-UNIFICATION-PRD.md). */
  description: string;
  /** The one-line question the mode answers (the query in words). */
  question: string;
  /** Commitment kinds this mode's query selects (empty = cross-kind by owner). */
  kinds: string[];
  /** Whether the mode slices by owner (Work=me, Verification=agent) vs by kind. */
  axis: "owner" | "kind";
  /**
   * For owner-axis modes, WHOSE commitments: `"me"` (the current human actor —
   * Work) or `"agent"` (any machine actor — Verification). Undefined for kind-axis
   * modes. This is what `loadModeData` filters on so Work shows *my* promises and
   * Verification shows the *machines'*, not the same undifferentiated set (Object
   * Model §3, COMMITMENT-OS §6).
   */
  ownerScope?: "me" | "agent";
  /** Objects-view sub-area filter chips (the primary kind shows by default). */
  subAreas: string[];
  /** Sub-areas that earn their own NESTED Mode Shell (C3: nesting capped at 1). */
  nested: string[];
}

export const MODES: readonly ModeDef[] = [
  {
    slug: "agents",
    label: "Verification",
    description: "Verifier workflows, decisions, receipts, evidence packs, and readiness.",
    question: "what evidence proves the work?",
    kinds: ["goal", "workflow", "run", "action"],
    axis: "owner",
    ownerScope: "agent",
    subAreas: ["verification", "readiness", "workflows", "runs"],
    nested: [],
  },
  {
    slug: "work",
    label: "Work",
    description: "Your commitments and inbox — what to do today, and what's waiting on you.",
    question: "what should I do today?",
    kinds: [],
    axis: "owner",
    ownerScope: "me",
    subAreas: ["commitments", "inbox", "planner"],
    nested: [],
  },
  {
    slug: "customers",
    label: "Customers",
    description: "Revenue, pipeline, and the accounts behind them.",
    question: "how is revenue & pipeline?",
    kinds: ["lead", "deal"],
    axis: "kind",
    subAreas: ["accounts", "opportunities", "leads", "campaigns", "support"],
    nested: [],
  },
  {
    slug: "product",
    label: "Product",
    description: "What's shipping — tasks, PRDs, releases, and the design system.",
    question: "how is delivery?",
    kinds: ["task", "prd", "release"],
    axis: "kind",
    subAreas: ["tasks", "prds", "releases", "design-system"],
    nested: [],
  },
  {
    slug: "operations",
    label: "Operations",
    description: "The back office — money, legal, hiring, and the people who run it.",
    // The back office: funded & compliant + who-are-we-hiring (the ATS is the
    // Hiring Domain here — 2026-06-17, reverting the brief LOCKED #2 promotion).
    question: "is the company funded, compliant & staffed?",
    kinds: ["transaction", "obligation", "job", "application", "scorecard"],
    axis: "kind",
    subAreas: ["finance", "legal", "team", "directory", "hiring"],
    nested: ["finance"],
  },
  {
    // The sixth mode — a personal knowledge corpus, not a business function. Owner
    // = me, but the objects are DROPPED-IN DATA (files/URLs/notes/archives), not
    // commitments; it keeps its own route (/personal) and carries no KPIs ("no
    // bars"). docs/personal/PRD.md.
    slug: "personal",
    label: "Personal",
    description: "Everything you've saved — files, links, notes, and archives.",
    question: "what have I saved?",
    kinds: [],
    axis: "owner",
    ownerScope: "me",
    subAreas: ["items", "collections"],
    nested: [],
  },
];

const MODE_BY_SLUG: Readonly<Record<ModeSlug, ModeDef>> = Object.fromEntries(
  MODES.map((m) => [m.slug, m]),
) as Record<ModeSlug, ModeDef>;

export function getMode(slug: string): ModeDef | null {
  return (MODE_BY_SLUG as Record<string, ModeDef>)[slug] ?? null;
}
export function isModeSlug(slug: string): slug is ModeSlug {
  return slug in MODE_BY_SLUG;
}

/**
 * The 9 workspace sections → their home mode. Domain sections fold by kind;
 * Operations gathers the back-office spine. Derived against SECTION_REGISTRY so a
 * missing entry is caught by the lockstep test (every canonical section maps).
 */
export const SECTION_MODE: Readonly<Record<string, ModeSlug>> = {
  marketing: "customers",
  sales: "customers",
  "customer-success": "customers",
  development: "product",
  design: "product",
  finance: "operations",
  hiring: "operations", // folded back into Operations as the Hiring Domain (2026-06-17, reverting LOCKED #2)
  operations: "operations",
  legal: "operations",
};

/** The 5 value streams → their home mode (streams are the prior nav spine). */
export const STREAM_MODE: Readonly<Record<StreamSlug, ModeSlug>> = {
  customer: "customers",
  product: "product",
  money: "operations",
  workforce: "operations",
  company: "operations",
};

/**
 * Which mode owns a given commitment kind (the "new feature has an obvious home"
 * answer). Owner-axis modes (work/agents) are NOT keyed here — they select by
 * owner, not kind; a kind's *domain* home is one of the by-kind modes (or agents
 * for the machine kinds).
 */
export function modeForKind(kind: string): ModeSlug | null {
  for (const m of MODES) if (m.kinds.includes(kind)) return m.slug;
  return null;
}

export interface ModePathOpts {
  /** Project slug → scoped tier (`/projects/<p>/<mode>`); omit → workspace tier (`/<mode>`). */
  projectSlug?: string | null;
  /** Defaults to the mode Dashboard (no `?view`). */
  view?: ModeView;
  /** Extra query params (e.g. a preserved deep `sub`, a sub-area filter). */
  extra?: Record<string, string>;
}

/** Build the canonical route for a mode at a scope+view (a projection request). */
export function modePath(mode: ModeSlug, opts: ModePathOpts = {}): string {
  const base = opts.projectSlug
    ? `/projects/${opts.projectSlug}/${mode}`
    : `/${mode}`;
  const params = new URLSearchParams();
  if (opts.view && opts.view !== "dashboard") params.set("view", opts.view);
  for (const [k, v] of Object.entries(opts.extra ?? {})) params.set(k, v);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** Lockstep guard data: every canonical section must resolve to a mode. */
export function unmappedSections(): string[] {
  return SECTION_REGISTRY.map((s) => s.slug).filter((slug) => !SECTION_MODE[slug]);
}
